import {
  Badge,
  Box,
  Checkbox,
  NumberInput,
  Pagination,
  Stack,
  Table,
  Text,
  VisuallyHidden,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import sharedListClasses from "../list/list.module.css";
import grid from "../ui/data-grid.module.css";
import { currencyFormat } from "../../utils";
import { dateFormat } from "../../utils/date";
import classes from "./RegisterPayment.module.css";
import { dueDay } from "../../utils/due-day";

export type AllocationRow = {
  id: string;
  refCode?: string | null;
  createdAt?: string | null;
  paymentDate?: string | null;
  confirmedAmount?: number | null;
  amountToPay: number;
  remainingDays: number | null;
};

export type AllocationEntry = {
  invoiceId: string;
  refCode: string;
  amountToPay: number;
  amount: number;
};

type Props = {
  rows: AllocationRow[];
  filteredLength: number;
  page: number;
  pageSize: number;
  allocations: Record<string, AllocationEntry>;
  onToggle: (row: AllocationRow) => void;
  onAmountChange: (invoiceId: string, value: number | string) => void;
  onPageChange: (page: number) => void;
};

const cents = (v: number) => Math.round(parseFloat(`${v}`) * 100);

function TermBadge({ days }: { days: number | null }) {
  const { t: ti } = useTranslation("invoice");

  if (days == null) {
    return (
      <Badge variant="light" color="gray" radius="xl" size="sm">
        {ti("regPayForm.term.none")}
      </Badge>
    );
  }
  if (days < 0) {
    return (
      <Badge variant="light" color="red" radius="xl" size="sm">
        {ti("regPayForm.term.overdue", { count: Math.abs(days) })}
      </Badge>
    );
  }
  if (days === 0) {
    return (
      <Badge variant="light" color="yellow" radius="xl" size="sm">
        {ti("regPayForm.term.today")}
      </Badge>
    );
  }
  if (days <= 7) {
    return (
      <Badge variant="light" color="yellow" radius="xl" size="sm">
        {ti("regPayForm.term.left", { count: days })}
      </Badge>
    );
  }
  return (
    <Badge variant="light" color="gray" radius="xl" size="sm">
      {ti("regPayForm.term.left", { count: days })}
    </Badge>
  );
}

export function AllocationTable({
  rows,
  filteredLength,
  page,
  pageSize,
  allocations,
  onToggle,
  onAmountChange,
  onPageChange,
}: Props) {
  const { t: ti } = useTranslation("invoice");
  const pages = Math.max(1, Math.ceil(filteredLength / pageSize));
  const from = filteredLength === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(filteredLength, page * pageSize);

  return (
    <Box className={classes.tableWrap}>
      <Table.ScrollContainer minWidth={1060}>
        <Table className={grid.table} data-density="compact" horizontalSpacing={0} verticalSpacing={0} withRowBorders={false}>
          <Table.Thead>
            <Table.Tr className={sharedListClasses.scrollRow}>
              <Table.Th className={`${sharedListClasses.stickyCheck} ${sharedListClasses.stickyEdge}`}>
                <VisuallyHidden>{ti("regPayForm.select")}</VisuallyHidden>
              </Table.Th>
              <Table.Th>{ti("regPayForm.col.invoice")}</Table.Th>
              <Table.Th>{ti("regPayForm.col.dueDate")}</Table.Th>
              <Table.Th>{ti("regPayForm.col.term")}</Table.Th>
              <Table.Th ta="right">{ti("regPayForm.col.invoiced")}</Table.Th>
              <Table.Th ta="right">{ti("regPayForm.col.remaining")}</Table.Th>
              <Table.Th ta="right" w={170}>
                {ti("regPayForm.col.allocate")}
              </Table.Th>
              <Table.Th ta="right">{ti("regPayForm.col.after")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => {
              const selected = !!allocations[row.id];
              const amount = selected ? allocations[row.id].amount : 0;
              const after = row.amountToPay - (selected ? amount : 0);
              const amountCents = cents(amount);
              const payCents = cents(row.amountToPay);

              let amountHint: { color: string; label: string } | null = null;
              if (selected) {
                if (amountCents === payCents && amountCents > 0) {
                  amountHint = { color: "green.7", label: ti("regPayForm.full") };
                } else if (amountCents > 0 && amountCents < payCents) {
                  amountHint = { color: "gray.8", label: ti("regPayForm.partial") };
                } else {
                  amountHint = { color: "red.7", label: ti("regPayForm.enterAmount") };
                }
              }

              return (
                <Table.Tr
                  key={row.id}
                  className={`${sharedListClasses.scrollRow} ${classes.row}`}
                  data-selected={selected || undefined}
                >
                  <Table.Td className={`${sharedListClasses.stickyCheck} ${sharedListClasses.stickyEdge}`}>
                    <Checkbox
                      color="brand"
                      size="sm"
                      radius="sm"
                      checked={selected}
                      onChange={() => onToggle(row)}
                      aria-label={row.refCode ?? row.id}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm" fw={500} ff="monospace">
                      {row.refCode ?? "-"}
                    </Text>
                    <Text fz="sm" c="dimmed">
                      {row.createdAt ? dateFormat(row.createdAt) : "-"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm" c="gray.8">
                      {dueDay(row.paymentDate) ?? "-"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <TermBadge days={row.remainingDays} />
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text fz="sm" c="gray.8" className={classes.num}>
                      {currencyFormat(row.confirmedAmount ?? 0, { withSymbol: false })}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text fz="sm" fw={500} className={classes.num}>
                      {currencyFormat(row.amountToPay, { withSymbol: false })}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right" w={170}>
                    {selected ? (
                      <Stack gap={2} align="flex-end">
                        <NumberInput
                          className={classes.allocateInput}
                          size="xs"
                          w={130}
                          hideControls
                          thousandSeparator=","
                          decimalScale={2}
                          min={0}
                          max={row.amountToPay}
                          clampBehavior="strict"
                          value={amount}
                          onChange={(v) => onAmountChange(row.id, v)}
                        />
                        {amountHint && (
                          <Text fz={14} fw={500} c={amountHint.color}>
                            {amountHint.label}
                          </Text>
                        )}
                      </Stack>
                    ) : (
                      <Text c="gray.7">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td ta="right">
                    {selected ? (
                      <Text
                        fz="sm"
                        fw={500}
                        className={classes.num}
                        c={cents(after) === 0 ? "gray.8" : "gray.9"}
                      >
                        {currencyFormat(after, { withSymbol: false })}
                      </Text>
                    ) : (
                      <Text fz="sm" c="gray.7" className={classes.num}>
                        {currencyFormat(row.amountToPay, { withSymbol: false })}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Box className={classes.pager}>
        <Text fz="sm" c="dimmed">
          {from}-{to} / {filteredLength}
        </Text>
        {pages > 1 ? (
          <Pagination size="sm" total={pages} value={page} onChange={onPageChange} />
        ) : null}
      </Box>
    </Box>
  );
}
